import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Avatar,
  InputAdornment,
  IconButton,
  Stack,
  CircularProgress,
  Divider,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupsIcon from "@mui/icons-material/Groups";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useAuth } from "../context/AuthContext";
import { useColorMode } from "../context/ThemeContext";

const FEATURES = [
  {
    icon: <GroupsIcon />,
    title: "Client Management",
    desc: "360° client profiles & documents",
  },
  {
    icon: <VerifiedUserIcon />,
    title: "Workflow & Audit",
    desc: "Team → Manager → Auditor pipeline",
  },
  {
    icon: <InsightsIcon />,
    title: "Smart Reports",
    desc: "Jobs, billing & productivity dashboards",
  },
];

export default function Login() {
  const { login } = useAuth();
  const { mode, toggle } = useColorMode();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (error) {
      setErr(error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        overflow: "hidden",
        bgcolor: "background.default",
        background: (t) =>
          t.palette.mode === "light"
            ? "linear-gradient(135deg, #eef2ff 0%, #f6f7fb 50%, #ede9fe 100%)"
            : "linear-gradient(135deg, #0b1020 0%, #111735 50%, #1e1b4b 100%)",
        p: { xs: 1.5, md: 2 },
        position: "relative",
      }}
    >
      <IconButton
        onClick={toggle}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 10,
          bgcolor: "background.paper",
          boxShadow: 1,
          "&:hover": { bgcolor: "background.paper" },
        }}
      >
        {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>

      <Paper
        elevation={8}
        sx={{
          m: "auto",
          width: "100%",
          maxWidth: 900,
          maxHeight: "100%",
          borderRadius: 3,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* LEFT BRAND PANEL */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            p: { xs: 2.5, md: 3.5 },
            color: "#fff",
            background:
              "linear-gradient(135deg, #4f46e5 0%, #6d28d9 50%, #9333ea 100%)",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -80,
              right: -80,
              width: 200,
              height: 200,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.08)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -60,
              left: -60,
              width: 160,
              height: 160,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.06)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ mb: 3 }}
            >
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.18)",
                  width: 42,
                  height: 42,
                  backdropFilter: "blur(8px)",
                }}
              >
                <AccountBalanceIcon sx={{ color: "#fff" }} />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  CA Portal
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.85, letterSpacing: 0.5, fontSize: 10 }}
                >
                  PRACTICE MANAGEMENT
                </Typography>
              </Box>
            </Stack>

            <Typography
              variant="h5"
              sx={{ fontWeight: 800, mb: 1, lineHeight: 1.25 }}
            >
              Run your CA practice, effortlessly.
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.9, mb: 2.5, display: "block", fontSize: 12 }}
            >
              Clients, services, workflows, audits & billing — all in one
              secure portal.
            </Typography>

            <Stack spacing={1.25}>
              {FEATURES.map((f) => (
                <Stack key={f.title} direction="row" spacing={1.25}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.18)",
                      width: 32,
                      height: 32,
                    }}
                  >
                    {React.cloneElement(f.icon, {
                      fontSize: "small",
                      sx: { color: "#fff" },
                    })}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontSize: 13 }}
                    >
                      {f.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.85, fontSize: 11 }}
                    >
                      {f.desc}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Typography
            variant="caption"
            sx={{
              position: "relative",
              zIndex: 1,
              opacity: 0.75,
              mt: 2,
              fontSize: 10,
            }}
          >
            © {new Date().getFullYear()} Kiyunet Technology · v1.0
          </Typography>
        </Box>

        {/* RIGHT LOGIN FORM */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3, md: 4.5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            bgcolor: "background.paper",
          }}
        >
          <Stack alignItems="flex-start" spacing={0.5} sx={{ mb: 2.5 }}>
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 40,
                height: 40,
                mb: 1,
              }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Welcome back
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sign in to your CA Portal account
            </Typography>
          </Stack>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

          <Box component="form" onSubmit={submit}>
            <TextField
              fullWidth
              size="small"
              label="Email address"
              placeholder="you@example.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Password"
              placeholder="Enter your password"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 1 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShow((s) => !s)}
                      edge="end"
                    >
                      {show ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: "right", mb: 2 }}>
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="medium"
              disabled={loading}
              sx={{
                py: 1.1,
                fontWeight: 700,
                fontSize: "0.85rem",
                borderRadius: 2,
                background:
                  "linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%)",
                boxShadow: "0 8px 20px rgba(79,70,229,0.35)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #4338ca 0%, #5b21b6 100%)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Sign in"
              )}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10 }}
              >
                SECURE LOGIN
              </Typography>
            </Divider>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "center", fontSize: 11 }}
            >
              Protected by 256-bit encryption. Your data stays safe.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
